import type { DiscLetter } from '../../constants/disc';
import { L, listJoin } from '../CoverQuiz/quizLang';
import type { QuizLang, Txt } from '../CoverQuiz/quizLang';

/**
 * 職涯測驗 第二部分 — 「描繪你的理想事業」: seven questions that build the
 * candidate's picture of the career (team, what draws them, their circle, how
 * they handle a "not now", family, milestones, what to ask the adviser), then a
 * blueprint built only from their own answers — no computed scores.
 *
 * On a `?t=` link the answers are submitted as `answers.part2` (a
 * `StoredPartTwo`: these answers plus the DISC letter the questions were worded
 * by); the server merges that object into the candidate's part-1 result (§16).
 */

export type Option<K extends string | number = string> = { key: K; icon: string; label: Txt; sub?: Txt };

export type PartTwoAnswers = {
  team: DiscLetter | null;
  attract: string[];
  circles: string[];
  contacts: number | null;
  reply: DiscLetter | null;
  family: number | null;
  milestones: string[];
  /** 踏出這一步，你最想得到什麼支援 — RECRUIT_CONCERNS keys (moved here from part 1, 2026-10-01). */
  concerns: string[];
  questions: string[];
};

export const EMPTY_PART_TWO: PartTwoAnswers = {
  team: null,
  attract: [],
  circles: [],
  contacts: null,
  reply: null,
  family: null,
  milestones: [],
  concerns: [],
  questions: []
};

/** Q1 — every type's team is offered; the candidate's own is listed first and tagged. */
export const TEAM_OPTIONS: Record<DiscLetter, Option<DiscLetter>> = {
  D: {
    key: 'D',
    icon: '🚀',
    label: L('一起衝目標、破紀錄的戰隊', '一起冲目标、破纪录的战队', 'A squad that chases targets and breaks records'),
    sub: L('目標明確，贏了一起慶祝', '目标明确，赢了一起庆祝', 'Clear goals, and we celebrate wins together')
  },
  I: {
    key: 'I',
    icon: '🎉',
    label: L(
      '氣氛好、常常一起慶祝的團隊',
      '气氛好、常常一起庆祝的团队',
      'A team with a great vibe that celebrates often'
    ),
    sub: L('開心工作，互相欣賞', '开心工作，互相欣赏', 'Happy at work, appreciating each other')
  },
  S: {
    key: 'S',
    icon: '🌳',
    label: L('互相支持、像家人一樣的團隊', '互相支持、像家人一样的团队', 'A supportive team that feels like family'),
    sub: L(
      '有人帶、有人撐，不會孤軍作戰',
      '有人带、有人撑，不会孤军作战',
      'Someone to guide and back you, never on your own'
    )
  },
  C: {
    key: 'C',
    icon: '📐',
    label: L(
      '專業、有系統、肯分享的團隊',
      '专业、有系统、肯分享的团队',
      'A professional, systematic team that shares what works'
    ),
    sub: L('有清楚的方法和數據可以跟著做', '有清楚的方法和数据可以跟着做', 'Clear methods and data to follow')
  }
};

export const teamOrderFor = (disc: DiscLetter): DiscLetter[] => [
  disc,
  ...(['D', 'I', 'S', 'C'] as DiscLetter[]).filter(letter => letter !== disc)
];

export const ATTRACT_OPTIONS: Option[] = [
  { key: 'freedom', icon: '🕊️', label: L('時間自由', '时间自由', 'Freedom over my time') },
  { key: 'income', icon: '💰', label: L('收入無上限', '收入无上限', 'No ceiling on income') },
  { key: 'help', icon: '❤️', label: L('幫助身邊的人', '帮助身边的人', 'Helping the people around me') },
  { key: 'growth', icon: '📚', label: L('培訓和成長', '培训和成长', 'Training and growth') },
  { key: 'team', icon: '🤝', label: L('一班好拍檔', '一班好拍档', 'Great teammates') },
  { key: 'travel', icon: '✈️', label: L('海外獎勵旅遊', '海外奖励旅游', 'Overseas incentive trips') }
];

export const CIRCLE_OPTIONS: Option[] = [
  { key: 'work', icon: '💼', label: L('同事／舊同事', '同事／旧同事', 'Colleagues / ex-colleagues') },
  { key: 'school', icon: '🎓', label: L('舊同學', '旧同学', 'Old schoolmates') },
  {
    key: 'hobby',
    icon: '🏸',
    label: L('興趣圈（運動、行山…）', '兴趣圈（运动、行山…）', 'Hobby groups (sports, hiking…)')
  },
  { key: 'parents', icon: '👨‍👩‍👧', label: L('家長圈', '家长圈', 'Other parents') },
  { key: 'church', icon: '⛪', label: L('教會／義工', '教会／义工', 'Church / volunteering') },
  { key: 'online', icon: '📱', label: L('網上社群', '网上社群', 'Online communities') },
  { key: 'biz', icon: '🏪', label: L('生意／客戶圈', '生意／客户圈', 'Business / client network') }
];

export const CONTACT_OPTIONS: Option<number>[] = [
  { key: 1, icon: '👤', label: L('20 人以下', '20 人以下', 'Under 20') },
  { key: 2, icon: '👥', label: L('20–50 人', '20–50 人', '20–50') },
  { key: 3, icon: '🧑‍🤝‍🧑', label: L('50–100 人', '50–100 人', '50–100') },
  { key: 4, icon: '🎉', label: L('100 人以上', '100 人以上', 'Over 100') }
];

/** Q5 — every option is a good answer; which one it is says how they talk. */
export const REPLY_OPTIONS: Option<DiscLetter>[] = [
  {
    key: 'D',
    icon: '📅',
    label: L(
      '問清楚他什麼時候會有需要，約個時間再聊',
      '问清楚他什么时候会有需要，约个时间再聊',
      'Ask when they might need it and set a time to talk again'
    )
  },
  {
    key: 'I',
    icon: '😊',
    label: L(
      '繼續做好朋友，他有需要自然會找我',
      '继续做好朋友，他有需要自然会找我',
      "Stay good friends; they'll come to me when they need it"
    )
  },
  {
    key: 'S',
    icon: '🎁',
    label: L(
      '送他一份有用的資料，慢慢建立信任',
      '送他一份有用的资料，慢慢建立信任',
      'Send them something useful and build trust over time'
    )
  },
  {
    key: 'C',
    icon: '🔍',
    label: L(
      '幫他看清楚現有保障，找出真正的缺口',
      '帮他看清楚现有保障，找出真正的缺口',
      'Help them review their current cover and find the real gaps'
    )
  }
];

export const FAMILY_OPTIONS: Option<number>[] = [
  { key: 4, icon: '💪', label: L('會全力支持我', '会全力支持我', "They'll back me all the way") },
  { key: 3, icon: '🙂', label: L('會尊重我的決定', '会尊重我的决定', "They'll respect my decision") },
  {
    key: 2,
    icon: '💬',
    label: L(
      '還沒聊過，想先多了解再跟他們說',
      '还没聊过，想先多了解再跟他们说',
      "Haven't talked yet; I want to learn more before telling them"
    )
  },
  {
    key: 1,
    icon: '🤔',
    label: L(
      '可能要我多解釋，他們才會放心',
      '可能要我多解释，他们才会放心',
      "I may need to explain more before they're comfortable"
    )
  }
];

export const MILESTONE_OPTIONS: Option[] = [
  { key: 'mdrt', icon: '🏅', label: L('第一年達到 MDRT', '第一年达到 MDRT', 'Reach MDRT in my first year') },
  { key: 'lead', icon: '👥', label: L('帶領自己的團隊', '带领自己的团队', 'Lead my own team') },
  { key: 'manager', icon: '📈', label: L('升做經理', '升做经理', 'Be promoted to manager') },
  {
    key: 'trip',
    icon: '✈️',
    label: L('去海外會議／獎勵旅遊', '去海外会议／奖励旅游', 'Go to an overseas conference / incentive trip')
  },
  { key: 'home', icon: '🏠', label: L('3 年內買樓', '3 年内买楼', 'Buy a home within 3 years') }
];

export const QUESTION_OPTIONS: Option[] = [
  { key: 'income', icon: '💵', label: L('收入怎麼算', '收入怎么算', 'How income is worked out') },
  {
    key: 'early',
    icon: '🌱',
    label: L('頭幾個月的收入怎麼安排', '头几个月的收入怎么安排', 'How income works in the first few months')
  },
  { key: 'training', icon: '📚', label: L('培訓和支援', '培训和支援', 'Training and support') },
  { key: 'culture', icon: '🤝', label: L('團隊文化', '团队文化', 'Team culture') },
  { key: 'day', icon: '☀️', label: L('一日工作流程', '一日工作流程', 'A typical working day') },
  { key: 'exam', icon: '📝', label: L('考牌要準備什麼', '考牌要准备什么', 'What the licence exam needs') }
];

/**
 * 你最想得到什麼支援 — asked as the support they want rather than the fear
 * behind it, but each option is one of RECRUIT_CONCERNS' keys, so the adviser
 * side (轉型顧慮, 化解 scripts) reads it as a concern. Part 2, not part 1: 找客戶,
 * 見客 and 考牌 say what the job is, which part 1 must not.
 */
export const SUPPORT_OPTIONS: Option[] = [
  {
    key: 'unstable',
    icon: '🌱',
    label: L('起步期的收入有清楚安排', '起步期的收入有清楚安排', 'A clear income plan for the early months')
  },
  {
    key: 'noClients',
    icon: '🧭',
    label: L('有人教我怎樣找客戶', '有人教我怎样找客户', 'Someone to teach me how to find clients')
  },
  {
    key: 'rejection',
    icon: '🤝',
    label: L('有師傅陪我一起見客', '有师傅陪我一起见客', 'A mentor to come to client meetings with me')
  },
  {
    key: 'family',
    icon: '👨‍👩‍👧',
    label: L('有人幫我向家人介紹這份工作', '有人帮我向家人介绍这份工作', 'Someone to help explain the job to my family')
  },
  {
    key: 'image',
    icon: '🏅',
    label: L('一個專業、有口碑的團隊', '一个专业、有口碑的团队', 'A professional team with a good name')
  },
  {
    key: 'notSales',
    icon: '💬',
    label: L('不用硬銷，用專業幫人', '不用硬销，用专业帮人', 'No hard selling, helping people with expertise')
  },
  {
    key: 'exam',
    icon: '📝',
    label: L('考牌有人教、有溫習資料', '考牌有人教、有温习资料', 'Exam coaching and study material')
  },
  {
    key: 'stableJob',
    icon: '🗺️',
    label: L('一個清楚的轉職計劃', '一个清楚的转职计划', 'A clear plan for switching careers')
  }
];

export const PART_TWO_LIMITS = { attract: 3, milestones: 2, concerns: 2, questions: 3 } as const;

export const HEADLINES: Record<DiscLetter, Txt> = {
  D: L(
    '你是天生的開拓者，這份事業讓你自己作主',
    '你是天生的开拓者，这份事业让你自己作主',
    "You're a natural trailblazer, and this career lets you be your own boss"
  ),
  I: L(
    '你是天生的感染者，這份事業靠你認識的每一個人',
    '你是天生的感染者，这份事业靠你认识的每一个人',
    "You're a natural motivator, and this career builds on everyone you know"
  ),
  S: L(
    '你是值得信賴的守護者，這份事業就是幫身邊的人',
    '你是值得信赖的守护者，这份事业就是帮身边的人',
    "You're a trusted guardian, and this career is about helping those around you"
  ),
  C: L(
    '你是專業可靠的顧問，這份事業重視你的分析力',
    '你是专业可靠的顾问，这份事业重视你的分析力',
    "You're a reliable professional, and this career values your analysis"
  )
};

export const PART_TWO_STEPS = 8;

/** Whether the answer for this step is complete enough to move on. */
export const partTwoStepReady = (step: number, a: PartTwoAnswers): boolean =>
  [
    Boolean(a.team),
    a.attract.length > 0,
    a.circles.length > 0 && a.contacts !== null,
    Boolean(a.reply),
    a.family !== null,
    a.concerns.length > 0,
    a.milestones.length > 0,
    a.questions.length > 0
  ][step] ?? false;

const pick = <K extends string | number>(options: Option<K>[], keys: K[]) =>
  keys.map(key => options.find(option => option.key === key)).filter((o): o is Option<K> => Boolean(o));
const one = <K extends string | number>(options: Option<K>[], key: K | null) =>
  key === null ? null : options.find(option => option.key === key) || null;

/** Labels for the blueprint and the adviser's summary, read straight off the answers. */
export const partTwoLabels = (a: PartTwoAnswers) => ({
  team: a.team ? TEAM_OPTIONS[a.team] : null,
  attract: pick(ATTRACT_OPTIONS, a.attract),
  circles: pick(CIRCLE_OPTIONS, a.circles),
  contacts: one(CONTACT_OPTIONS, a.contacts),
  reply: one(REPLY_OPTIONS, a.reply),
  family: one(FAMILY_OPTIONS, a.family),
  milestones: pick(MILESTONE_OPTIONS, a.milestones),
  concerns: pick(SUPPORT_OPTIONS, a.concerns),
  questions: pick(QUESTION_OPTIONS, a.questions)
});

/** 90 日起步路線圖, from the first circle they named and their first milestone, in `lang`. */
export const partTwoRoadmap = (a: PartTwoAnswers, lang: QuizLang): Array<{ when: string; what: string }> => {
  const l = partTwoLabels(a);
  const circle = (l.circles[0]?.label || L('身邊朋友', '身边朋友', 'friends around you'))[lang];
  const milestone = (l.milestones[0]?.label || L('你的目標', '你的目标', 'your goal'))[lang];
  return [
    {
      when: L('第 1 個月', '第 1 个月', 'Month 1')[lang],
      what: L(
        '和顧問見面，了解收入和培訓',
        '和顾问见面，了解收入和培训',
        'Meet your adviser to learn about income and training'
      )[lang]
    },
    {
      when: L('第 2 個月', '第 2 个月', 'Month 2')[lang],
      what: L(
        `準備考牌；由「${circle}」開始，列出第一批想幫的人`,
        `准备考牌；由「${circle}」开始，列出第一批想帮的人`,
        `Prepare for the licence exam; starting with "${circle}", list the first people you'd like to help`
      )[lang]
    },
    {
      when: L('第 3 個月', '第 3 个月', 'Month 3')[lang],
      what: L(
        `有師傅陪同見客，完成第一份保單，向「${milestone}」出發`,
        `有师傅陪同见客，完成第一份保单，向「${milestone}」出发`,
        `Meet clients with a mentor, complete your first policy, and head for "${milestone}"`
      )[lang]
    }
  ];
};

/**
 * What the adviser sees: the candidate's answers grouped by the recruit radar's
 * items (the adviser scores them — nothing here is a score), plus flags.
 * 「自律」has no question and is left to the adviser.
 */
export const partTwoAdviserNotes = (a: PartTwoAnswers, lang: QuizLang) => {
  const l = partTwoLabels(a);
  const list = (items: Option<string | number>[]) =>
    listJoin(
      items.map(item => item.label[lang]),
      lang
    );
  const sep = lang === 'en' ? '; ' : '；';
  const draws = L('最吸引：', '最吸引：', 'Draws them most: ')[lang];
  const milestones = L('里程碑：', '里程碑：', 'Milestones: ')[lang];
  const inTouch = L('常聯絡 ', '常联络 ', 'In touch with ')[lang];
  const rows: Array<{ axis: string; text: string }> = [
    {
      axis: L('動機', '动机', 'Motivation')[lang],
      text: `${draws}${list(l.attract) || '-'}${l.milestones.length ? `${sep}${milestones}${list(l.milestones)}` : ''}`
    },
    {
      axis: L('人脈', '人脉', 'Network')[lang],
      text: `${list(l.circles) || '-'}${l.contacts ? `${sep}${inTouch}${l.contacts.label[lang]}` : ''}`
    },
    { axis: L('溝通', '沟通', 'Communication')[lang], text: l.reply ? l.reply.label[lang] : '-' },
    { axis: L('家庭', '家庭', 'Family')[lang], text: l.family ? l.family.label[lang] : '-' },
    { axis: L('支援', '支援', 'Support')[lang], text: list(l.concerns) || '-' }
  ];
  const flags: string[] = [];
  if (a.family === 1) {
    flags.push(
      L(
        '家人可能有保留：可以邀請家人一起出席講座，或準備「行業前景」資料。',
        '家人可能有保留：可以邀请家人一起出席讲座，或准备「行业前景」资料。',
        'Family may have reservations: invite them to a talk together, or prepare material on the industry outlook.'
      )[lang]
    );
  }
  return { rows, flags, topics: l.questions.map(q => q.label[lang]) };
};

/** What is stored under `answers.part2`: the answers, plus the DISC letter the questions followed. */
export type StoredPartTwo = PartTwoAnswers & { disc: DiscLetter | null };

const DISC_KEYS: DiscLetter[] = ['D', 'I', 'S', 'C'];
const discOr = (value: unknown): DiscLetter | null =>
  DISC_KEYS.includes(value as DiscLetter) ? (value as DiscLetter) : null;
const keysOf = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((key): key is string => typeof key === 'string') : [];
const numberOr = (value: unknown): number | null => (typeof value === 'number' ? value : null);

/**
 * `answers.part2` from a stored career result, read defensively (the server
 * stores answers raw). Null when the candidate has not done 第二部分.
 */
export const partTwoAnswersOf = (answers: unknown): StoredPartTwo | null => {
  if (!answers || typeof answers !== 'object') return null;
  const raw = (answers as Record<string, unknown>).part2;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const a = raw as Record<string, unknown>;
  return {
    disc: discOr(a.disc),
    team: discOr(a.team),
    attract: keysOf(a.attract),
    circles: keysOf(a.circles),
    contacts: numberOr(a.contacts),
    reply: discOr(a.reply),
    family: numberOr(a.family),
    milestones: keysOf(a.milestones),
    concerns: keysOf(a.concerns),
    questions: keysOf(a.questions)
  };
};
